// Taşeron portalı — Supplier Self-Service (api-contract §13.6) TanStack Query anahtarları.
//
// Supplier **yalnız kendi** taşeron firmasını görür (contractor kimliği token/bağdan
// çözülür — istemci göndermez). Bu yüzden anahtarlar contractor uuid TAŞIMAZ; aktif firma
// (`companyId`) değişince (switcher → `/auth/switch-company`) sorgular yeniden çekilir —
// companyId anahtara katılır ki firma değişiminde önbellek karışmasın.

import type { DocumentAnalysisListFilter } from '@isgportal/shared';

export const supplierContractorKeys = {
  /** Tüm supplier self-service sorgularının kök öneki. */
  all: () => ['supplier-contractor'] as const,
  /** Kendi taşeron firması (firma bilgisi + uygunluk özeti) — aktif firmaya kapsanır. */
  contractor: (companyId: number | null) =>
    ['supplier-contractor', 'contractor', companyId] as const,
  /** Kendi uygunluk belgeleri. */
  documents: (companyId: number | null) => ['supplier-contractor', 'documents', companyId] as const,
  /** Kendi firmasının araç kaydı (§23.2) — aktif firmaya kapsanır. */
  vehicles: (companyId: number | null) => ['supplier-contractor', 'vehicles', companyId] as const,
  /** Kendi firmasının personeli. */
  personnel: (companyId: number | null) => ['supplier-contractor', 'personnel', companyId] as const,
  /**
   * Tek bir personelin önbellek öneki — aşağıdaki `personnelDocuments`in (`[..., 'personnel',
   * companyId, userUuid, 'documents']`) kişi başına ÖNEKİDİR, bir sorgunun anahtarı DEĞİL. Web
   * tekil `GET /supplier/contractor/personnel/{userUuid}`'i (§13.6'da var) hiç çağırmaz: çekmece
   * ve düzenleyici (`usePersonnelEditor`) kişiyi `personnel` listesinden okur. Aktif firma +
   * `userUuid` anahtara katılır.
   *
   * **Belgeleri de kapsayan aşırı-invalidasyon KASITLIDIR, "düzeltilmez"** (S26 plan madde 9 / web
   * güvenlik incelemesi F — anahtar önekleri): bu öneki `exact:false` (TanStack varsayılanı) ile
   * geçersiz kılmak `personnelDocuments`ı da tazeler — düzenleme ve firmadan çıkarma sonrası
   * istenen budur (kişi çıkarılınca belgeleri de görünürlüğünü kaybeder, D7/D8 — S26 plan).
   */
  personnelByUuid: (companyId: number | null, userUuid: string) =>
    ['supplier-contractor', 'personnel', companyId, userUuid] as const,
  /**
   * Kendi taşeronundaki bir personelin belgeleri (§15.2 yol B; KVKK; kişi bazlı). Aktif firma +
   * `userUuid` anahtara katılır ki firma/personel değişiminde önbellek karışmasın.
   */
  personnelDocuments: (companyId: number | null, userUuid: string) =>
    ['supplier-contractor', 'personnel', companyId, userUuid, 'documents'] as const,
  /**
   * `supplier-contractor` erişim sondası (gate) — hafif `GET /supplier/contractor` probu. İki
   * katmanlı authz: yalnız **Yetkili** (grant var) geçer; **Personeli** (yalnız `supplier-self`)
   * 403 alır → yönetim ekranları gizlenir.
   */
  access: (companyId: number | null) => ['supplier-contractor', 'access', companyId] as const,
  /**
   * Tek bir doküman analizinin durumu + (terminalse) sonucu (api-contract §20.1,
   * `GET .../document-analyses/{uuid}`). Aktif firma + analiz `uuid`'i anahtara katılır —
   * hem firma değişiminde önbellek karışmasın (S17-D12) hem her analiz kendi yoklama
   * yaşam döngüsünü (`useDocumentAnalysis`) taşısın.
   */
  analysis: (companyId: number | null, analysisUuid: string) =>
    ['supplier-contractor', 'document-analyses', companyId, analysisUuid] as const,
  /**
   * Analiz LİSTESİ (api-contract §20.3, `GET .../document-analyses`, YENİ S19) —
   * `awaiting-review` | `active` | `all` | `archived` (YENİ S25-A4) süzgeciyle. Aktif firma +
   * süzgeç anahtara katılır: firma değişiminde önbellek karışmasın VE her süzgeç kendi
   * girdisini taşısın — `active`'in bir sayfa yenilemesinde tray'i yeniden bağlaması
   * `awaiting-review`'in Belgeler şeridini GEÇERSİZ KILMAZ (ayrı anahtar, ayrı önbellek girdisi).
   *
   * `pageSize` (YENİ, S25 web kod incelemesi #5) YALNIZ AÇIKÇA verildiğinde anahtara EKLENİR —
   * verilmezse anahtar ÖNCEKİYLE BİREBİR aynıdır (`pageSize` yokluğu ile `undefined` AYNI
   * davranır). Bu, iki gerçeği AYNI ANDA korur: (1) `useDocumentAnalysisList(filter)`'ı
   * varsayılan boyutla çağıran her yer (`PendingAnalysesStrip`, `StrandedAnalysesStrip`'in
   * `all`'ı, `AnalysisInboxMenu`'nün geçersiz kılmaları, `DocumentAnalysisProvider`'ın
   * `active`'i) ÖNCEKİ TEK önbellek girdisini PAYLAŞMAYA devam eder — `DocumentAnalysisInboxPage`
   * `pageSize` ile daha geniş bir sayfa istediğinde bu paylaşılan girdiyi EZMEZ/onunla
   * ÇAKIŞMAZ, kendi ayrı girdisine yazar. (2) `queryClient.invalidateQueries({queryKey:
   * analyses(companyId, filter)})` (parametresiz, HER çağıran) TanStack'in varsayılan ÖNEK
   * eşlemesiyle (`exact: false`) bu daha uzun/`pageSize`li anahtarı da KAPSAR — geçersiz kılma
   * döngüleri (`AnalysisInboxMenu`, `AnalysisReviewPane`, `AnalysisInboxRail`,
   * `StrandedAnalysesStrip`) DEĞİŞMEDEN paginlenmiş girdiyi de tazeler.
   */
  analyses: (companyId: number | null, filter: DocumentAnalysisListFilter, pageSize?: number) =>
    pageSize != null
      ? (['supplier-contractor', 'document-analyses', companyId, filter, pageSize] as const)
      : (['supplier-contractor', 'document-analyses', companyId, filter] as const),
} as const;
